/**
 * Query-key factory + cached Carpool lookups (statuses, campuses, event types, public settings).
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useRidesLookupsService, type RidesLookupsView, type RidesLookupItem } from '@actschurch/ccms-sdk';

export const ridesKeys = {
  all: ['rides'] as const,
  lookups: () => ['rides', 'lookups'] as const,
  dashboard: () => ['rides', 'dashboard'] as const,
  drivers: (filters: unknown) => ['rides', 'drivers', filters] as const,
  driver: (userId: string) => ['rides', 'driver', userId] as const,
  verifications: (filters: unknown) => ['rides', 'verifications', filters] as const,
  bookings: (filters: unknown) => ['rides', 'bookings', filters] as const,
  booking: (id: number) => ['rides', 'booking', id] as const,
  listings: (filters: unknown) => ['rides', 'listings', filters] as const,
  disputes: (filters: unknown) => ['rides', 'disputes', filters] as const,
  security: (filters: unknown) => ['rides', 'security', filters] as const,
  campuses: () => ['rides', 'campuses'] as const,
  events: (from?: string | null, to?: string | null) => ['rides', 'events', from ?? '', to ?? ''] as const,
  settings: () => ['rides', 'settings'] as const,
  copy: () => ['rides', 'copy'] as const,
  lookupTable: (table: string) => ['rides', 'lookup-table', table] as const,
  /** Uploaded documents (254g): review queue pages, one member's documents (admin / security desk). */
  documents: (filters: unknown) => ['rides', 'documents', 'list', filters] as const,
  userDocuments: (userId: string) => ['rides', 'documents', 'user', userId] as const,
  securityDocuments: (userId: string) => ['rides', 'documents', 'security', userId] as const,
  documentLog: (rideUploadId: number) => ['rides', 'documents', 'log', rideUploadId] as const,
  documentTypes: () => ['rides', 'document-types'] as const,
};

const toOptions = (items: RidesLookupItem[] | undefined, includeInactive = true) =>
  (items ?? [])
    .filter((i) => includeInactive || i.isActive !== false)
    .map((i) => ({ value: String(i.id), label: i.name ?? '' }));

export function useRidesLookups() {
  const lookupsService = useRidesLookupsService();
  const query = useQuery<RidesLookupsView>({
    queryKey: ridesKeys.lookups(),
    queryFn: () => lookupsService.getLookups(),
    staleTime: 5 * 60_000,
  });

  const options = useMemo(() => {
    const l = query.data;
    return {
      verificationTypes: toOptions(l?.verificationTypes),
      verificationStatuses: toOptions(l?.verificationStatuses),
      availabilityStatuses: toOptions(l?.availabilityStatuses),
      bookingStatuses: toOptions(l?.bookingStatuses),
      disputeStatuses: toOptions(l?.disputeStatuses),
      eventTypes: toOptions(l?.eventTypes),
      sexes: toOptions(l?.sexes),
      /** Active ride campuses — label + address hint. */
      campuses: (l?.campuses ?? [])
        .filter((c) => c.isActive)
        .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))
        .map((c) => ({ value: String(c.locationID), label: c.name })),
      countries: (l?.countries ?? []).map((c) => ({ value: c.code, label: c.name })),
    };
  }, [query.data]);

  const nameOf = useMemo(() => {
    const l = query.data;
    const map = (items?: RidesLookupItem[]) => new Map((items ?? []).map((i) => [i.id, i.name]));
    const campuses = new Map((l?.campuses ?? []).map((c) => [c.locationID, c.name]));
    return {
      campus: (id: number | null | undefined) => (id != null ? campuses.get(id) : undefined),
      eventType: (() => {
        const m = map(l?.eventTypes);
        return (id: number | null | undefined) => (id != null ? m.get(id) : undefined);
      })(),
    };
  }, [query.data]);

  return { ...query, lookups: query.data, options, nameOf, campuses: query.data?.campuses ?? [] };
}
